import { deleteItem, fetchItems, postItem, putItem } from './queries';
import { JourneyMapDraft } from '../types/journeyMap';
import { journeyMapToDraft } from './journeyMapDraftAdapter';
import { ensureUniqueJourneyMapSlug, slugifyJourneyMapName } from './journeyMap/slug';

export type DroppedLink = {
  tempId: string;
  label: string;
  from_item_temp_id: string | null;
  to_item_temp_id: string | null;
  reason: string;
};

export type LinkConflict = {
  tempId: string;
  fromItemId: number;
  toItemId: number;
  link_type: string;
  reason: string;
};

export type SaveJourneyMapResult = {
  map: any;
  droppedLinks: DroppedLink[];
  linkConflicts: LinkConflict[];
};

const linkTypeOf = (value: any): string => String(value || 'default');

type DeleteError = Error & {
  status?: number;
  body?: any;
};

function asArray<T = any>(value: any): T[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Rows under a `(parent, order)` unique index must be re-saved REORDER-SAFE. A plain sequential loop
 * of full-record PUTs transiently duplicates an `order` whenever two rows swap slots (row A is written
 * to an order row B still holds), which the DB unique index rejects with the DRF error
 * "… order must make a unique set". MySQL can't DEFER the constraint and there is no bulk/transaction
 * reorder endpoint, so callers first PARK every reordered row at a temporary order in a disjoint range
 * (`final + offset`, larger than any live order), then write the final orders in a second pass — no
 * intermediate state ever holds a duplicate. `parkingOffset` returns that collision-free offset.
 */
function parkingOffset(orders: number[]): number {
  return orders.reduce((max, o) => (o > max ? o : max), 0) + 1;
}

async function deleteItemIfExists(endpoint: string) {
  try {
    await deleteItem(endpoint);
  } catch (error: any) {
    const typedError = error as DeleteError;
    const message = String(typedError?.message || '').toLowerCase();
    const bodyText = JSON.stringify(typedError?.body || '').toLowerCase();

    // Treat already-deleted records as success.
    if (
      typedError?.status === 404 ||
      message.includes('404') ||
      message.includes('not found') ||
      bodyText.includes('not found') ||
      bodyText.includes('item not found')
    ) {
      return;
    }

    throw error;
  }
}

async function hardDeleteItemProgressByJourneyMapItemId(journeyMapItemId: number) {
  const rows = asArray(
    await fetchItems(
      'db/journey-map-item-progress/',
      { journey_map_item: journeyMapItemId },
      { includePageSize: false }
    ).catch(() => [])
  );

  for (const row of rows) {
    if (row?.id != null) {
      await deleteItemIfExists(`db/journey-map-item-progress/${row.id}/`);
    }
  }
}

/**
 * Resolve the slug to persist. Prefer an explicit slug the admin already set (sanitized), else
 * derive one from the name; then make it unique against every other map so the Go backend — which
 * does NOT run Django's auto-slug `save()` — never receives a blank or duplicate slug.
 */
async function resolveJourneyMapSlug(draft: JourneyMapDraft): Promise<string> {
  const explicit = slugifyJourneyMapName(draft.slug || '');
  const base = explicit || slugifyJourneyMapName(draft.name || '');
  if (!base) return '';

  const existingMaps = asArray(
    await fetchItems('db/journey-map/', {}, { includePageSize: false }).catch(() => []),
  );

  const taken = new Set(
    existingMaps
      .filter((m: any) => Number(m?.id) !== Number(draft.id))
      .map((m: any) => String(m?.slug || '').toLowerCase())
      .filter(Boolean),
  );

  return ensureUniqueJourneyMapSlug(base, taken);
}

export async function saveJourneyMapDraft(
  draft: JourneyMapDraft,
): Promise<SaveJourneyMapResult> {
  const slug = await resolveJourneyMapSlug(draft);

  const mapPayload = {
    id: draft.id,
    name: draft.name,
    slug,
    image_url: draft.image_url || '',
    description: draft.description || '',
    journey_type: draft.journey_type || null,
    owner: draft.owner || null,
    version: draft.version || 1,
    publication_status: draft.publication_status || 'draft',
    is_active: draft.is_active ?? true,
    estimated_duration_minutes: draft.estimated_duration_minutes ?? null,
    audience_rules: draft.audience_rules || {},
    metadata: {
      ...(draft.metadata || {}),
      background_image_url: draft.background_image_url || '',
      section_delimiters: draft.section_delimiters || [],
    },
    available_from: draft.available_from || null,
    available_to: draft.available_to || null,
  };

  const savedMap = draft.id
    ? await putItem('db/journey-map', mapPayload)
    : await postItem('db/journey-map', mapPayload);

  const mapId = savedMap.id;

  const existingItems = mapId
    ? asArray(await fetchItems('db/journey-map-item/', { journey_map: mapId }))
    : [];

  const existingLinks = mapId
    ? asArray(await fetchItems('db/journey-map-item-link/', { journey_map: mapId }))
    : [];

  const existingPathGroups = mapId
    ? asArray(await fetchItems('db/journey-map-path-group/', { journey_map: mapId }))
    : [];

  const existingPathGroupItems = mapId
    ? asArray(
      await fetchItems('db/journey-map-path-group-item/', {
        journey_map: mapId,
      }).catch(() => []),
    )
    : [];

  const activeItems = draft.items
    .filter((item) => !item.isDeleted)
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const activeLinks = draft.links.filter((link) => !link.isDeleted);
  const activePathGroups = draft.pathGroups.filter((group) => !group.isDeleted);

  const keptExistingItemIds = new Set(
    activeItems.filter((item) => !!item.id).map((item) => Number(item.id)),
  );

  // 1) Delete removed DB items BEFORE inserting new ones to avoid unique(order) conflicts.
  // Also hard-delete all item progress for removed items.
  for (const oldItem of existingItems) {
    if (!keptExistingItemIds.has(oldItem.id)) {
      await hardDeleteItemProgressByJourneyMapItemId(Number(oldItem.id));
      await deleteItemIfExists(`db/journey-map-item/${oldItem.id}/`);
    }
  }

  const savedItemIdByTempId = new Map<string, number>();

  // 2) Update existing items — reorder-safe (see parkingOffset): park any reordered row at a
  // temporary disjoint order first, then write final orders, so the unique(journey_map, order)
  // index is never transiently violated when items swap slots.
  const existingDraftItems = activeItems.filter((item) => !!item.id);

  const buildItemPayload = (item: any, order: number) => ({
    id: item.id,
    journey_map: mapId,
    name: item.name,
    description: item.description || '',
    order,
    item_type: item.item_type || 'content',
    is_required: item.is_required ?? true,
    estimated_duration_minutes: item.estimated_duration_minutes ?? null,
    content_ref_type: item.content_ref_type || '',
    content_ref_id: item.content_ref_id || '',
    provider: item.provider || '',
    launch_url: item.launch_url || '',
    completion_mode: item.completion_mode || 'external_sync',
    provider_config: item.provider_config || {},
    prerequisite_logic: item.prerequisite_logic || {},
    metadata: {
      ...(item.metadata || {}),
      position_x: item.position_x ?? null,
      position_y: item.position_y ?? null,
    },
    passing_score: item.passing_score ?? null,
    due_offset_days: item.due_offset_days ?? null,
    available_from: item.available_from || null,
    available_to: item.available_to || null,
  });

  const dbItemOrderById = new Map(
    existingItems.map((e: any) => [Number(e.id), Number(e.order ?? 0)]),
  );
  const reorderedItems = existingDraftItems.filter(
    (item) => dbItemOrderById.get(Number(item.id)) !== (item.order ?? 0),
  );
  if (reorderedItems.length) {
    const offset = parkingOffset([
      ...existingItems.map((e: any) => Number(e.order ?? 0)),
      ...existingDraftItems.map((item) => Number(item.order ?? 0)),
    ]);
    for (const item of reorderedItems) {
      await putItem('db/journey-map-item', buildItemPayload(item, (item.order ?? 0) + offset));
    }
  }

  for (const item of existingDraftItems) {
    const savedItem = await putItem('db/journey-map-item', buildItemPayload(item, item.order));
    savedItemIdByTempId.set(item.tempId, savedItem.id);
  }

  // 3) Create new items
  const newDraftItems = activeItems.filter((item) => !item.id);
  for (const item of newDraftItems) {
    const itemMetadata = {
      ...(item.metadata || {}),
      position_x: item.position_x ?? null,
      position_y: item.position_y ?? null,
    };

    const payload = {
      journey_map: mapId,
      name: item.name,
      description: item.description || '',
      order: item.order,
      item_type: item.item_type || 'content',
      is_required: item.is_required ?? true,
      estimated_duration_minutes: item.estimated_duration_minutes ?? null,
      content_ref_type: item.content_ref_type || '',
      content_ref_id: item.content_ref_id || '',
      provider: item.provider || '',
      launch_url: item.launch_url || '',
      completion_mode: item.completion_mode || 'external_sync',
      provider_config: item.provider_config || {},
      prerequisite_logic: item.prerequisite_logic || {},
      metadata: itemMetadata,
      passing_score: item.passing_score ?? null,
      due_offset_days: item.due_offset_days ?? null,
      available_from: item.available_from || null,
      available_to: item.available_to || null,
    };

    const savedItem = await postItem('db/journey-map-item', payload);
    savedItemIdByTempId.set(item.tempId, savedItem.id);
  }

  // 4) Links: delete stale links first so relinking abandoned pairs works cleanly
  const droppedLinks: DroppedLink[] = [];
  const resolvedActiveLinkPairs = new Set<string>();
  const resolvedActiveLinks = activeLinks
    .map((link) => {
      const fromItemId =
        link.from_item ||
        (link.from_item_temp_id ? savedItemIdByTempId.get(link.from_item_temp_id) : undefined);

      const toItemId =
        link.to_item ||
        (link.to_item_temp_id ? savedItemIdByTempId.get(link.to_item_temp_id) : undefined);

      if (!fromItemId || !toItemId) {
        // A link whose endpoints can't be resolved to saved item ids would otherwise
        // vanish with no error. Record it so the caller can surface the failure.
        droppedLinks.push({
          tempId: link.tempId,
          label: link.label || '',
          from_item_temp_id: link.from_item_temp_id ?? null,
          to_item_temp_id: link.to_item_temp_id ?? null,
          reason: !fromItemId
            ? 'source item could not be resolved to a saved id'
            : 'target item could not be resolved to a saved id',
        });
        return null;
      }

      const key = `${Number(fromItemId)}::${Number(toItemId)}::${linkTypeOf(link.link_type)}`;
      resolvedActiveLinkPairs.add(key);

      return {
        draftLink: link,
        fromItemId: Number(fromItemId),
        toItemId: Number(toItemId),
      };
    })
    .filter(Boolean) as Array<{
    draftLink: any;
    fromItemId: number;
    toItemId: number;
  }>;

  if (droppedLinks.length) {
    console.warn(
      `[saveJourneyMapDraft] ${droppedLinks.length} link(s) dropped — unresolved endpoints.`,
      { droppedLinks, savedItemTempIds: [...savedItemIdByTempId.keys()] },
    );
  }

  // Remove stale links BEFORE upsert/create
  for (const oldLink of existingLinks) {
    const oldFrom = Number(oldLink?.from_item);
    const oldTo = Number(oldLink?.to_item);
    const oldKey = `${oldFrom}::${oldTo}::${linkTypeOf(oldLink?.link_type)}`;

    if (!resolvedActiveLinkPairs.has(oldKey)) {
      await deleteItemIfExists(`db/journey-map-item-link/${oldLink.id}/`);
    }
  }

  const savedLinkIds = new Set<number>();
  const linkConflicts: LinkConflict[] = [];

  for (const resolved of resolvedActiveLinks) {
    const { draftLink: link, fromItemId, toItemId } = resolved;

    const payload = {
      id: link.id,
      journey_map: mapId,
      from_item: fromItemId,
      to_item: toItemId,
      label: link.label || '',
      link_type: link.link_type || 'default',
      condition_type: link.condition_type || 'always',
      condition_config: {
        ...(link.condition_config || {}),
        source_handle: link.source_handle ?? null,
        target_handle: link.target_handle ?? null,
        edge_shape: link.edge_shape ?? null,
        edge_thickness: link.edge_thickness ?? null,
        edge_dash_style: link.edge_dash_style ?? null,
        edge_animated: link.edge_animated ?? null,
      },
      priority: link.priority ?? 0,
      is_active: link.is_active ?? true,
      source_handle: link.source_handle ?? null,
      target_handle: link.target_handle ?? null,
    };

    try {
      const savedLink = link.id
        ? await putItem('db/journey-map-item-link', payload)
        : await postItem('db/journey-map-item-link', payload);

      if (savedLink?.id) savedLinkIds.add(savedLink.id);
    } catch (error: any) {
      const message = String(error?.message || '').toLowerCase();
      const body = JSON.stringify(error?.response?.data || error?.data || error?.body || '').toLowerCase();
      const looksLikeUnique =
        message.includes('unique') ||
        body.includes('unique') ||
        body.includes('already exists') ||
        body.includes('must make a unique set');

      if (!looksLikeUnique) throw error;

      const matches = asArray(
        await fetchItems(
          'db/journey-map-item-link/',
          {
            journey_map: mapId,
            from_item: fromItemId,
            to_item: toItemId,
          },
          { includePageSize: false }
        ).catch(() => [])
      );

      // Only update in place when the existing row is the SAME logical edge (same link_type).
      // A different link_type colliding on the pair means the backend still enforces
      // unique(from_item, to_item) — overwriting would silently destroy the other edge.
      const sameTypeExisting = matches.find(
        (m: any) => linkTypeOf(m?.link_type) === linkTypeOf(link.link_type),
      );

      if (sameTypeExisting?.id) {
        const savedLink = await putItem('db/journey-map-item-link', {
          ...payload,
          id: sameTypeExisting.id,
        });
        if (savedLink?.id) savedLinkIds.add(savedLink.id);
      } else if (matches.length) {
        // Parallel edge rejected by the backend's pair-uniqueness constraint.
        linkConflicts.push({
          tempId: link.tempId,
          fromItemId,
          toItemId,
          link_type: linkTypeOf(link.link_type),
          reason:
            'backend rejected a second link between the same two items — the ' +
            'journey-map-item-link uniqueness constraint must include link_type',
        });
      } else {
        throw error;
      }
    }
  }

  if (linkConflicts.length) {
    console.warn(
      `[saveJourneyMapDraft] ${linkConflicts.length} parallel link(s) rejected by the ` +
        'backend pair-uniqueness constraint.',
      { linkConflicts },
    );
  }

  // 5) Path groups
  const savedPathGroupIds = new Set<number>();
  const savedPathGroupIdByTempId = new Map<string, number>();

  const buildGroupPayload = (group: any, order: number) => ({
    id: group.id,
    journey_map: mapId,
    name: group.name,
    description: group.description || '',
    group_type: group.group_type || 'all_of',
    min_required: group.min_required ?? 1,
    is_required: group.is_required ?? false,
    order,
    metadata: group.metadata || {},
  });

  // Park reordered existing groups first so the write pass below can't transiently violate the
  // unique(journey_map, order) index (same reorder-safe approach as items).
  const dbGroupOrderById = new Map(
    existingPathGroups.map((g: any) => [Number(g.id), Number(g.order ?? 0)]),
  );
  const reorderedGroups = activePathGroups.filter(
    (g) => g.id && dbGroupOrderById.get(Number(g.id)) !== (g.order ?? 0),
  );
  if (reorderedGroups.length) {
    const offset = parkingOffset([
      ...existingPathGroups.map((g: any) => Number(g.order ?? 0)),
      ...activePathGroups.map((g) => Number(g.order ?? 0)),
    ]);
    for (const group of reorderedGroups) {
      await putItem('db/journey-map-path-group', buildGroupPayload(group, (group.order ?? 0) + offset));
    }
  }

  for (const group of activePathGroups) {
    const savedGroup = group.id
      ? await putItem('db/journey-map-path-group', buildGroupPayload(group, group.order))
      : await postItem('db/journey-map-path-group', buildGroupPayload(group, group.order));

    if (savedGroup?.id) {
      savedPathGroupIds.add(savedGroup.id);
      savedPathGroupIdByTempId.set(group.tempId, savedGroup.id);
    }
  }

  for (const oldGroup of existingPathGroups) {
    if (!savedPathGroupIds.has(oldGroup.id)) {
      await deleteItemIfExists(`db/journey-map-path-group/${oldGroup.id}/`);
    }
  }

  // 6) Path group items
  const savedPathGroupItemIds = new Set<number>();

  const dbGroupItemOrderById = new Map(
    existingPathGroupItems.map((gi: any) => [Number(gi.id), Number(gi.order ?? 0)]),
  );

  for (const group of activePathGroups) {
    const pathGroupId = group.id || savedPathGroupIdByTempId.get(group.tempId);
    if (!pathGroupId) continue;

    const activeGroupItems = group.items.filter((item) => !item.isDeleted);

    const buildGroupItemPayload = (groupItem: any, journeyMapItemId: number, order: number) => ({
      id: groupItem.id,
      path_group: pathGroupId,
      journey_map_item: journeyMapItemId,
      order,
    });

    // Resolve each group item's target journey-map item id up front (needed by both passes).
    const resolvedGroupItems = activeGroupItems
      .map((groupItem) => {
        const journeyMapItemId =
          groupItem.journey_map_item ||
          (groupItem.journey_map_item_temp_id
            ? savedItemIdByTempId.get(groupItem.journey_map_item_temp_id)
            : undefined);
        return { groupItem, journeyMapItemId };
      })
      .filter((r) => !!r.journeyMapItemId) as Array<{ groupItem: any; journeyMapItemId: number }>;

    // Park reordered existing group items first — the constraint here is unique(path_group, order).
    const reorderedGroupItems = resolvedGroupItems.filter(
      ({ groupItem }) =>
        groupItem.id && dbGroupItemOrderById.get(Number(groupItem.id)) !== (groupItem.order ?? 0),
    );
    if (reorderedGroupItems.length) {
      const offset = parkingOffset([
        ...existingPathGroupItems
          .filter((gi: any) => Number(gi.path_group) === Number(pathGroupId))
          .map((gi: any) => Number(gi.order ?? 0)),
        ...resolvedGroupItems.map((r) => Number(r.groupItem.order ?? 0)),
      ]);
      for (const { groupItem, journeyMapItemId } of reorderedGroupItems) {
        await putItem(
          'db/journey-map-path-group-item',
          buildGroupItemPayload(groupItem, journeyMapItemId, (groupItem.order ?? 0) + offset),
        );
      }
    }

    for (const { groupItem, journeyMapItemId } of resolvedGroupItems) {
      const savedGroupItem = groupItem.id
        ? await putItem(
            'db/journey-map-path-group-item',
            buildGroupItemPayload(groupItem, journeyMapItemId, groupItem.order),
          )
        : await postItem(
            'db/journey-map-path-group-item',
            buildGroupItemPayload(groupItem, journeyMapItemId, groupItem.order),
          );

      if (savedGroupItem?.id) savedPathGroupItemIds.add(savedGroupItem.id);
    }
  }

  for (const oldGroupItem of existingPathGroupItems) {
    if (!savedPathGroupItemIds.has(oldGroupItem.id)) {
      await deleteItemIfExists(`db/journey-map-path-group-item/${oldGroupItem.id}/`);
    }
  }

  return { map: savedMap, droppedLinks, linkConflicts };
}

/**
 * Fetch a journey map's items, links and path groups and build a fully-hydrated draft.
 * Used both when opening a map for editing and to re-hydrate the builder after a save, so
 * that items/links carry their real server ids (otherwise a subsequent save deletes and
 * recreates every item because the draft still holds tempId-only records).
 */
export async function loadJourneyMapDraft(map: any): Promise<JourneyMapDraft> {
  const mapId = map?.id;
  if (!mapId) return journeyMapToDraft(map);

  const [itemResults, linkResults, pathGroupResults, pathGroupItemResults] =
    await Promise.all([
      fetchItems('db/journey-map-item/', { journey_map: mapId }).catch(() => []),
      fetchItems('db/journey-map-item-link/', { journey_map: mapId }).catch(() => []),
      fetchItems('db/journey-map-path-group/', { journey_map: mapId }).catch(() => []),
      fetchItems('db/journey-map-path-group-item/', { journey_map: mapId }).catch(() => []),
    ]);

  const items = asArray(itemResults);
  const links = asArray(linkResults);
  const pathGroups = asArray(pathGroupResults);
  const pathGroupItems = asArray(pathGroupItemResults);

  const hydratedPathGroups = pathGroups.map((group: any) => ({
    ...group,
    path_group_items: pathGroupItems.filter(
      (groupItem: any) => Number(groupItem?.path_group) === Number(group?.id),
    ),
  }));

  return journeyMapToDraft({
    ...map,
    journey_map_items: items,
    item_links: links,
    journey_map_item_links: links,
    path_groups: hydratedPathGroups,
    journey_map_path_groups: hydratedPathGroups,
    journey_map_path_group_items: pathGroupItems,
  });
}